import React, { useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { useParams } from 'react-router-dom';
import { isEmpty, isNil } from 'ramda';

import { deleteInvoice, markAsPaid } from '../../../mutations/invoices';
import StorePresenter from '../../../presenters/StorePresenter';
import InvoicePresenter from '../../../presenters/store/InvoicePresenter';
import { StoreProvider, Dialog, ErrorFlash } from '../../../components';

import InvoiceTable from './components/InvoiceTable';
import AddInvoiceModal from './components/AddInvoiceModal';

const InvoiceListView = (props) => {
  const { storeLists, relay, brand } = props;
  const invoices = storeLists.invoices;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(false);
  const [errors, setErrors] = useState(null);
  const handleDialogOpen = (invoice) => {
    setEditedInvoice(invoice);
    setIsDialogOpen(true);
  };
  const handleDialogClose = () => {
    setEditedInvoice(null);
    setIsDialogOpen(false);
  };
  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleInvoiceDelete = () => {
    const params = {
      storeId: StorePresenter.id(brand),
      id: InvoicePresenter.id(editedInvoice),
    };
    deleteInvoice(relay.environment, params, storeLists.id, {
      onCompleted: (response) => handleSubmitSuccess(response, 'deleteInvoice'),
      onError: (error) => console.error(`commit failed: ${error}`),
    });
  };

  const handleMarkAsPaid = (invoice) => {
    const params = {
      storeId: StorePresenter.id(brand),
      id: InvoicePresenter.id(invoice),
    };

    markAsPaid(relay.environment, params, {
      onCompleted: (response) => handleSubmitSuccess(response, 'markAsPaid'),
    });
  };

  const handleSubmitSuccess = (response, actionName) => {
    const { errors } = response[actionName];
    if (!isNil(errors)) {
      setErrors(errors);
    } else {
      handleDialogClose();
    }
  };

  return (
    <div className="invoices">
      <div className="header">
        {!isNil(errors) && <ErrorFlash errors={errors} message={errors} />}
        <div className="form-header">
          <h2>
            {`Invoices for ${StorePresenter.name(brand)} `}
            <span className="amount">({invoices.length})</span>
          </h2>
          <div className="form-header-actions">
            <div
              id="invoice"
              className="button -add-button"
              onClick={handleModalOpen}
            >
              Create Invoice
            </div>
          </div>
        </div>
      </div>
      {isEmpty(invoices) ? (
        <div>The brand has no invoices</div>
      ) : (
        <InvoiceTable
          invoices={invoices}
          brand={brand}
          onDialogOpen={handleDialogOpen}
          onMarkAsPaid={handleMarkAsPaid}
        />
      )}
      <AddInvoiceModal
        isModalOpen={isModalOpen}
        onModalClose={handleModalClose}
        brand={brand}
        listsId={storeLists.id}
        relay={relay}
      />
      <Dialog
        className="deletion"
        isOpen={isDialogOpen}
        message={`Are you sure you want to remove invoice #${InvoicePresenter.id(
          editedInvoice
        )} for ${StorePresenter.name(brand)}?`}
        onConfirm={handleInvoiceDelete}
        onCancel={handleDialogClose}
      />
    </div>
  );
};

const InvoiceListContainer = createFragmentContainer(InvoiceListView, {
  brand: graphql`
    fragment InvoiceList_brand on Store {
      id
      name
    }
  `,
  invoiceSettings: graphql`
    fragment InvoiceList_invoiceSettings on InvoiceSettings {
      wiringInstructions
      contactPerson
      contactPhoneNumber
      contactEmail
    }
  `,
  storeLists: graphql`
    fragment InvoiceList_storeLists on StoreLists
    @argumentDefinitions(storeId: { type: "ID!" }) {
      id
      invoices(storeId: $storeId) {
        id
        startDate
        endDate
        amountDue
        state
        invoiceNumber
        invoicePdfUrl
      }
    }
  `,
});

const InvoiceList = () => {
  const { brandId } = useParams();

  const preprocessParams = () => {
    const variables = {
      storeId: brandId,
    };

    return variables;
  };
  return (
    <StoreProvider
      container={InvoiceListContainer}
      modern
      preprocessParams={preprocessParams}
      query={graphql`
        query InvoiceListQuery($storeId: ID!) {
          storeLists {
            ...InvoiceList_storeLists @arguments(storeId: $storeId)
          }
          brand: store(id: $storeId) {
            ...InvoiceList_brand
          }
          invoiceSettings {
            ...InvoiceList_invoiceSettings
          }
        }
      `}
    />
  );
};

export default InvoiceList;
